import { googleGeocode as untypedGoogleGeocode } from '@rsos/components/capstone/common/Map/utils';
import {
  isGoogleGeocoderReady,
  MapsNotReadyError,
} from '../reverseGeocode/googleGeocoderReady';
import type { GeocodeCandidate } from './rsosGeocoder';

interface LatLngBoundsLiteral {
  northeast: { lat: number; lng: number };
  southwest: { lat: number; lng: number };
}

// An untyped JS module whose JSDoc marks `bounds` required; it is optional.
const googleGeocode = untypedGoogleGeocode as unknown as (
  address: string,
  bounds?: LatLngBoundsLiteral,
) => Promise<google.maps.GeocoderResult[]>;

/** `_serviceUrl` marker on place candidates from the Google Geocoder. */
export const GOOGLE_PLACE_SOURCE = 'google-geocoder';

/** Half-width in degrees (~55 km) of the bounds that bias results toward the map center. */
const BIAS_DEGREES = 0.5;

const SCORE_BY_LOCATION_TYPE: Record<string, number> = {
  ROOFTOP: 100,
  RANGE_INTERPOLATED: 90,
  GEOMETRIC_CENTER: 80,
  APPROXIMATE: 70,
};

const POINT_ADDRESS_TYPES = ['street_address', 'premise', 'subpremise'];

const toGoogleCandidate = (
  result: google.maps.GeocoderResult,
): GeocodeCandidate => {
  const types = result.types || [];
  const isPoi =
    types.includes('point_of_interest') || types.includes('establishment');
  let addrType = 'Locality';
  if (isPoi) addrType = 'POI';
  else if (types.some(t => POINT_ADDRESS_TYPES.includes(t)))
    addrType = 'PointAddress';
  else if (types.includes('route')) addrType = 'StreetName';

  const address = result.formatted_address;
  const comma = address.indexOf(',');
  return {
    address,
    location: {
      x: result.geometry.location.lng(),
      y: result.geometry.location.lat(),
    },
    attributes: {
      Addr_type: addrType,
      ShortLabel: comma > 0 ? address.slice(0, comma) : address,
      Place_addr: comma > 0 ? address.slice(comma + 1).trim() : '',
    },
    score: SCORE_BY_LOCATION_TYPE[result.geometry.location_type] ?? 70,
    _serviceUrl: GOOGLE_PLACE_SOURCE,
    _token: null,
  };
};

/**
 * Forward geocode free text with the Google Maps Geocoder. Rejects with
 * {@link MapsNotReadyError} before the Google map has loaded the Geocoder, and
 * resolves to [] when Google found nothing.
 */
export const googleForwardGeocode = async ({
  text,
  latitude,
  longitude,
  limit = 5,
}: {
  text: string;
  latitude?: number;
  longitude?: number;
  limit?: number;
}): Promise<GeocodeCandidate[]> => {
  if (!isGoogleGeocoderReady()) {
    throw new MapsNotReadyError(
      'Google Maps JavaScript API is not loaded. Cannot geocode.',
    );
  }
  const bounds: LatLngBoundsLiteral | undefined =
    latitude != null && longitude != null
      ? {
          northeast: {
            lat: latitude + BIAS_DEGREES,
            lng: longitude + BIAS_DEGREES,
          },
          southwest: {
            lat: latitude - BIAS_DEGREES,
            lng: longitude - BIAS_DEGREES,
          },
        }
      : undefined;
  try {
    const results = await googleGeocode(text, bounds);
    return (results || []).slice(0, limit).map(toGoogleCandidate);
  } catch {
    // googleGeocode rejects on every non-OK status, ZERO_RESULTS included.
    return [];
  }
};
